import {SketchRelationKind} from '../relation-compatibility/SketchRelationKinds';
import type {SketchRelationDefinition} from '../relationDefinition';
import {TangentConstraint} from '../relationDefinition/TangentConstraint';
import {ThreePointAngleConstraint} from '../relationDefinition/point/ThreePointAngleConstraint';
import {SlotFixConstraint} from '../relationDefinition/slot/SlotFixConstraint';
import {SlotEqualConstraint} from '../relationDefinition/slot/SlotEqualConstraint';
import {createSketchPatternedConstraintFromFormulaPayload} from '../relationDefinition/pattern/SketchPatternedConstraint';
import {
    LineCoincidentConstraint,
    LineCollinearConstraint,
    LineEqualConstraint,
    LineFixConstraint,
    LineHorizontalConstraint,
    LineParallelConstraint,
    LinePerpendicularConstraint,
    LineVerticalConstraint,
    LineAngleConstraint,
    LineIntersectionConstraint,
} from '../relationDefinition/line';
import {MidpointConstraint} from '../relationDefinition/line/MidPointConstraint';
import {
    PointCoincidentConstraint,
    PointCurveCoincidentConstraint,
    PointDistanceConstraint,
    PointFixConstraint,
    PointMidpointConstraint,
} from '../relationDefinition/point';
import {SketchEntitySymmetricConstraint} from '../relationDefinition/SketchEntitySymmetricConstraint';
import {
    ArcRadiusConstraint,
    CircleDiameterConstraint,
    CircleConcentricConstraint,
    CircleCoradialConstraint,
    CircleCurveLengthEqualConstraint,
    CircleEqualConstraint,
    CircleFixConstraint,
} from '../relationDefinition/circle';
import type {SketchConstraintState, SketchSolverIslandSnapshot} from '../../../../history/solver';
import type {SketchSolverRuntimeGraph} from './SketchSolverObjectCollector';
import type {ISketchObject} from '../../../../mesh/sketch/ISketchObject';

/**
 * 可恢复约束构造器。
 */
type RestorableConstraintConstructor = new () => SketchRelationDefinition;

/**
 * 可写约束容器。
 */
interface RestorableSketchRelationContainer {
    /** 添加 solver 约束。 */
    readonly addRelation: (constraint: SketchRelationDefinition) => void;
}

/**
 * 可恢复目标对象。
 */
interface RestorableConstraintTarget {
    /** 目标 ID。 */
    readonly targetId: string;
    /** 目标运行时对象。 */
    readonly target: unknown;
}

/**
 * 解析 relation kind。
 *
 * @param relationKind snapshot 中的关系类型。
 * @returns 可识别的草图关系类型；无法识别时返回 null。
 */
function resolveSketchRelationKind(relationKind: string): SketchRelationKind | null {
    const relationKinds = Object.values(SketchRelationKind) as string[];
    return relationKinds.includes(relationKind) ? relationKind as SketchRelationKind : null;
}

/**
 * 读取目标对象类型。
 *
 * @param target 目标运行时对象。
 * @returns 目标对象类型。
 */
function readTargetObjectType(target: unknown): string {
    const objectType = target && typeof target === 'object'
        ? Reflect.get(target, 'objectType')
        : null;
    return typeof objectType === 'string' ? objectType : '';
}

/**
 * 判断目标是否为点。
 *
 * @param target 目标运行时对象。
 * @returns 是否为点。
 */
function isPointTarget(target: unknown): boolean {
    return Boolean(target && typeof target === 'object' && Reflect.get(target, 'isSketchPointObject') === true);
}

/**
 * 判断目标是否为圆系曲线。
 *
 * @param target 目标运行时对象。
 * @returns 是否为圆系曲线。
 */
function isCircleLikeTarget(target: unknown): boolean {
    return ['Circle', 'Arc', 'Ellipse'].includes(readTargetObjectType(target));
}

/**
 * 判断目标是否为 Coincident 标准曲线目标。
 *
 * @param target 目标运行时对象。
 * @returns 是否为线/圆/圆弧/椭圆。
 */
function isCoincidentCurveLikeTarget(target: unknown): boolean {
    return ['Line', 'CenterLine', 'Circle', 'Arc', 'Ellipse'].includes(readTargetObjectType(target));
}

/**
 * 解析约束构造器。
 *
 * @param state 约束状态。
 * @param targets 约束直接目标。
 * @returns 约束构造器。
 */
function resolveConstraintConstructor(
    state: SketchConstraintState,
    targets: readonly RestorableConstraintTarget[],
): RestorableConstraintConstructor {
    const relationKind = resolveSketchRelationKind(state.relationKind);
    if (!relationKind) {
        throw new Error(`无法恢复草图几何关系：未知关系类型 ${state.relationKind}。`);
    }

    if (relationKind === SketchRelationKind.Fix) {
        if (targets.some(({target}) => isPointTarget(target))) {
            return PointFixConstraint;
        }
        return targets.some(({target}) => isCircleLikeTarget(target)) ? CircleFixConstraint : LineFixConstraint;
    }

    if (relationKind === SketchRelationKind.Equal) {
        return targets.some(({target}) => isCircleLikeTarget(target)) ? CircleEqualConstraint : LineEqualConstraint;
    }

    if (relationKind === SketchRelationKind.Coincident) {
        // 恢复规则必须与创建规则保持一致，只把“一个点 + 一条曲线”恢复为点曲线重合约束。
        const pointTargetCount = targets.filter(({target}) => isPointTarget(target)).length;
        const curveTargetCount = targets.filter(({target}) => isCoincidentCurveLikeTarget(target)).length;
        if (targets.length === 2 && pointTargetCount === 1 && curveTargetCount === 1) {
            return PointCurveCoincidentConstraint;
        }
        return targets.every(({target}) => isPointTarget(target)) ? PointCoincidentConstraint : LineCoincidentConstraint;
    }

    switch (relationKind) {
        case SketchRelationKind.Horizontal:
            return LineHorizontalConstraint;
        case SketchRelationKind.Vertical:
            return LineVerticalConstraint;
        case SketchRelationKind.Parallel:
            return LineParallelConstraint;
        case SketchRelationKind.Perpendicular:
            return LinePerpendicularConstraint;
        case SketchRelationKind.Collinear:
            return LineCollinearConstraint;
        case SketchRelationKind.Midpoint:
            return targets.some(({target}) => isPointTarget(target)) ? PointMidpointConstraint : MidpointConstraint;
        case SketchRelationKind.Symmetric:
            return SketchEntitySymmetricConstraint;
        case SketchRelationKind.Tangent:
            return TangentConstraint;
        case SketchRelationKind.Distance:
            return PointDistanceConstraint;
        case SketchRelationKind.Angle:
            return LineAngleConstraint;
        // 倒角落地时创建的两线交点关系（焦点控制点随同回写）。
        case SketchRelationKind.Intersection:
            return LineIntersectionConstraint;
        case SketchRelationKind.Coradial:
            return CircleCoradialConstraint;
        case SketchRelationKind.Concentric:
            return CircleConcentricConstraint;
        case SketchRelationKind.EqualCurveLength:
            return CircleCurveLengthEqualConstraint;
        case SketchRelationKind.Radius:
            return ArcRadiusConstraint;
        case SketchRelationKind.ThreePointAngle:
            return ThreePointAngleConstraint;
        case SketchRelationKind.FixSlot:
            return SlotFixConstraint;
        case SketchRelationKind.EqualSlot:
            return SlotEqualConstraint;
        case SketchRelationKind.Diameter:
            return CircleDiameterConstraint;
        default:
            throw new Error(`无法恢复草图几何关系：${state.relationKind} 尚未注册约束构造器。`);
    }
}

/**
 * 按稳定 ID（旧快照再回退运行时 ID）解析当前关系实例。
 *
 * @param graph 当前运行时图。
 * @param state 目标关系状态。
 * @returns 当前实例；不存在时返回 null。
 */
function resolveRuntimeConstraint(
    graph: SketchSolverRuntimeGraph,
    state: SketchConstraintState,
): SketchRelationDefinition | null {
    const byPersistentId = graph.constraintObjects.get(state.constraintId);

    if (byPersistentId) {
        return byPersistentId;
    }
    if (typeof state.runtimeId !== 'number') {
        return null;
    }

    return Array.from(graph.constraintObjects.values())
        .find((constraint) => constraint.id === state.runtimeId) ?? null;
}

/**
 * 恢复关系运行状态。
 *
 * @param constraint 运行时关系。
 * @param state snapshot 关系状态。
 */
function restoreConstraintRuntimeState(
    constraint: SketchRelationDefinition,
    state: SketchConstraintState,
): void {
    constraint.setSuppressed(state.states.includes('suppressed'));
    constraint.setBranchHint(state.branchHint ?? null);
}

/**
 * 写回约束身份。
 *
 * 设计说明：
 * Redo 重建关系时只恢复跨会话稳定标识；当前会话数字 ID 由新实例自行分配，
 * 禁止历史语义依赖运行时创建顺序。
 *
 * @param constraint 新建约束。
 * @param state 约束状态。
 */
function restoreConstraintIdentity(
    constraint: SketchRelationDefinition,
    state: SketchConstraintState,
): void {
    Reflect.set(constraint, 'persistentId', state.constraintId);
}

/**
 * 读取目标对象的约束容器。
 *
 * @param target 目标运行时对象。
 * @param targetId 目标 ID。
 * @returns 约束容器。
 */
function readRestorableSketchRelationContainer(
    target: unknown,
    targetId: string,
): RestorableSketchRelationContainer {
    const constraints = target && typeof target === 'object'
        ? Reflect.get(target, 'constraints')
        : null;
    const addRelation = constraints && typeof constraints === 'object'
        ? Reflect.get(constraints, 'addRelation')
        : null;

    if (typeof addRelation !== 'function') {
        throw new Error(`无法恢复草图几何关系：目标 ${targetId} 缺少可写约束容器。`);
    }

    return {
        addRelation(constraint) {
            addRelation.call(constraints, constraint);
        },
    };
}

/**
 * 从直接目标引用恢复约束目标。
 *
 * @param graph 当前运行时图。
 * @param state 约束状态。
 * @returns 目标对象列表。
 */
function resolveConstraintTargetsFromRefs(
    graph: SketchSolverRuntimeGraph,
    state: SketchConstraintState,
): readonly RestorableConstraintTarget[] {
    const targetRefs = state.targetRefs ?? [];
    if (targetRefs.length <= 0) {
        return [];
    }

    return targetRefs.map((targetRef): RestorableConstraintTarget => {
        const target = targetRef.targetKind === 'entity'
            ? graph.entityObjects.get(targetRef.targetId)
            : targetRef.targetKind === 'point'
                ? graph.pointObjects.get(targetRef.targetId)
                : graph.referenceObjects.get(targetRef.targetId);

        if (!target) {
            throw new Error(`无法恢复草图几何关系 ${state.constraintId}：缺少目标 ${targetRef.targetId}。`);
        }

        return {
            targetId: targetRef.targetId,
            target,
        };
    });
}

/**
 * 从旧 snapshot 字段恢复约束目标。
 *
 * 设计说明：
 * 旧历史记录没有 `targetRefs`。为了兼容当前会话内尚未迁移的记录，优先使用实体目标；
 * 只有没有实体目标时才使用点目标，避免把边关联点误挂成线/圆约束的直接目标。
 *
 * @param graph 当前运行时图。
 * @param state 约束状态。
 * @returns 目标对象列表。
 */
function resolveConstraintTargetsFromLegacyFields(
    graph: SketchSolverRuntimeGraph,
    state: SketchConstraintState,
): readonly RestorableConstraintTarget[] {
    const entityTargets = state.targetEntityIds.map((targetId): RestorableConstraintTarget => {
        const target = graph.entityObjects.get(targetId);
        if (!target) {
            throw new Error(`无法恢复草图几何关系 ${state.constraintId}：缺少实体目标 ${targetId}。`);
        }

        return {targetId, target};
    });

    if (entityTargets.length > 0) {
        return entityTargets;
    }

    return state.targetPointIds.map((targetId): RestorableConstraintTarget => {
        const target = graph.pointObjects.get(targetId);
        if (!target) {
            throw new Error(`无法恢复草图几何关系 ${state.constraintId}：缺少点目标 ${targetId}。`);
        }

        return {targetId, target};
    });
}

/**
 * 解析约束重建目标。
 *
 * @param graph 当前运行时图。
 * @param state 约束状态。
 * @returns 目标对象列表。
 */
function resolveConstraintTargets(
    graph: SketchSolverRuntimeGraph,
    state: SketchConstraintState,
): readonly RestorableConstraintTarget[] {
    const targetsFromRefs = resolveConstraintTargetsFromRefs(graph, state);
    if (targetsFromRefs.length > 0) {
        return targetsFromRefs;
    }

    return resolveConstraintTargetsFromLegacyFields(graph, state);
}

/**
 * 重建单条缺失约束。
 *
 * @param graph 当前运行时图。
 * @param state 约束状态。
 */
function restoreMissingConstraint(
    graph: SketchSolverRuntimeGraph,
    state: SketchConstraintState,
): void {
    const existing = resolveRuntimeConstraint(graph, state);

    if (existing) {
        restoreConstraintRuntimeState(existing, state);
        return;
    }

    const targets = resolveConstraintTargets(graph, state);

    if (targets.length <= 0) {
        throw new Error(`无法恢复草图几何关系 ${state.constraintId}：snapshot 中没有目标对象。`);
    }

    const constraint = state.relationKind === SketchRelationKind.Patterned
        ? createSketchPatternedConstraintFromFormulaPayload(state.formulaPayload)
        : new (resolveConstraintConstructor(state, targets))();

    restoreConstraintIdentity(constraint, state);
    restoreConstraintRuntimeState(constraint, state);
    // 先原子校验完整目标元组，再写入任一容器，避免第三个目标失败后前两个容器残留半条关系。
    constraint.bindTargets(targets.map(({target}) => target as ISketchObject));
    targets.forEach(({target, targetId}) => {
        readRestorableSketchRelationContainer(target, targetId).addRelation(constraint);
    });
}

/**
 * 重建 solver island snapshot 中缺失的几何关系。
 *
 * @param graph 当前运行时图。
 * @param snapshot 目标 solver island snapshot。
 */
export function restoreMissingSketchRelationDefinitions(
    graph: SketchSolverRuntimeGraph,
    snapshot: SketchSolverIslandSnapshot,
): void {
    snapshot.constraintStates.forEach((state) => {
        restoreMissingConstraint(graph, state);
    });
}
